/**
 * Componente shared per la scelta di uno slot di sopralluogo.
 *
 * Gestisce internamente:
 *  - Fetch dell'ultimo site_operator della pratica (per proporre "mantieni stesso")
 *  - Step `same_op_ask` (se esiste un operator precedente)
 *  - Navigazione mese + fetch slot dal calendario (public o filtrato per operator)
 *  - Selezione di uno slot + notifica al parent via `onSlotChange(slot, operatorId|null)`
 *
 * Iter208.25 — UI a griglia mensile stile Google Calendar (allineato a
 * `PublicAppointmentCalendar`). Cliccando su un giorno con disponibilità si
 * espandono gli orari sotto la griglia.
 *
 * Usato da `RequestSiteVisitDialog` (nuovo sopralluogo) e `RescheduleVisitDialog`
 * (cambio data). I due dialog restano wrapper sottili che gestiscono solo submit
 * verso l'endpoint corretto + UI success.
 */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { User, ArrowLeft, CaretLeft, CaretRight } from "@phosphor-icons/react";

const monthNames = [
  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre",
];
const WD_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WD_MIN = ["L", "M", "M", "G", "V", "S", "D"];

/** Costruisce le 42 celle (6x7) a partire dal primo giorno del mese `anchor`. */
function buildMonthGrid(year, month) {
  const first = new Date(year, month - 1, 1);
  const isoDow = (first.getDay() + 6) % 7; // Lun=0
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - isoDow);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}
function pad2(n) { return String(n).padStart(2, "0"); }
function dateKey(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function SiteVisitCalendar({
  practice,
  onSlotChange,
  // Testid prefix per differenziare tra request/reschedule (default "svc")
  testidPrefix = "svc",
  // Se true, chiediamo l'operatore precedente al mount (default true).
  // Il calendario stesso propone "mantieni operator" o "cambia operator".
  askPreviousOperator = true,
  // Durata slot custom (in minuti). Se non fornita, il backend usa quella del
  // servizio (tipicamente 60 min per il primo sopralluogo). Il flow "Richiedi
  // sopralluogo secondario" passa 30 min per allineare gli slot mostrati con
  // la durata effettiva dello step iniettato.
  durationOverride = null,
  // Se true, nasconde i nomi operatori nel calendario (usato dal PRO in
  // "Cambia data": deve vedere un calendario pubblico anonimo).
  hideOperatorName = false,
}) {
  const [step, setStep] = useState("loading"); // loading | same_op_ask | calendar
  const [previousOp, setPreviousOp] = useState(null);
  const [pickedOperatorId, setPickedOperatorId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Iter163 — resiliente a `practice.service_id` come oggetto (dump del
  // servizio invece del solo ID). Estrae sempre una stringa.
  const rawSvcId = practice?.service_id;
  const serviceId = typeof rawSvcId === "string"
    ? rawSvcId
    : (rawSvcId && (rawSvcId._id || rawSvcId.id)) || "";
  const pid = practice?._id || practice?.id;

  // Bootstrap
  useEffect(() => {
    if (!pid) return;
    if (!askPreviousOperator) {
      setStep("calendar");
      return;
    }
    setStep("loading");
    (async () => {
      try {
        const r = await api.get(`/practices/${pid}/previous-visit-operator`);
        if (r.data?.operator) {
          setPreviousOp(r.data.operator);
          setStep("same_op_ask");
        } else {
          setStep("calendar");
        }
      } catch {
        setStep("calendar");
      }
    })();
  }, [pid, askPreviousOperator]);

  // Fetch slots quando cambia mese/operator
  useEffect(() => {
    if (step !== "calendar" || !serviceId) return;
    setLoadingSlots(true);
    setSlots([]);
    setSelectedSlot(null);
    if (onSlotChange) onSlotChange(null, pickedOperatorId);
    const opParam = pickedOperatorId ? `&operator_id=${pickedOperatorId}` : "";
    const durParam = durationOverride ? `&duration_override=${durationOverride}` : "";
    api.get(`/calendar/combined-availability?service_id=${serviceId}&year=${year}&month=${month}${opParam}${durParam}`)
      .then(r => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [step, serviceId, year, month, pickedOperatorId, durationOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedByDate = useMemo(() => {
    const g = {};
    for (const s of slots) {
      if (!g[s.date]) g[s.date] = [];
      g[s.date].push(s);
    }
    return g;
  }, [slots]);

  const [expandedDate, setExpandedDate] = useState(null);
  // Espandi automaticamente il primo giorno con slot quando cambia il mese
  useEffect(() => {
    const days = Object.keys(groupedByDate).sort();
    setExpandedDate(days[0] || null);
    setSelectedSlot(null);
  }, [groupedByDate]);

  const changeMonth = (delta) => {
    let ny = year, nm = month + delta;
    if (nm > 12) { nm = 1; ny += 1; }
    if (nm < 1) { nm = 12; ny -= 1; }
    setYear(ny); setMonth(nm);
  };

  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  };

  const chooseSlot = (slot) => {
    setSelectedSlot(slot);
    if (onSlotChange) onSlotChange(slot, pickedOperatorId);
  };

  const monthGridCells = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const today = new Date();

  if (step === "loading") {
    return <div className="py-12 text-center text-slate-500 text-sm">Caricamento…</div>;
  }

  if (step === "same_op_ask" && previousOp) {
    return (
      <div className="space-y-4 py-2">
        <div className="rounded-lg border-2 border-blue-200 bg-blue-50/60 p-4">
          <div className="text-sm font-semibold text-slate-900 mb-2">
            Sopralluogo precedente svolto da:
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center">
              <User size={20} weight="bold" className="text-blue-700" />
            </div>
            <div>
              <div className="font-medium text-slate-900" data-testid={`${testidPrefix}-prev-op-name`}>
                {previousOp.first_name} {previousOp.last_name}
              </div>
              <div className="text-xs text-slate-500">{previousOp.email}</div>
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-700">
          Vuoi affidare il sopralluogo allo <b>stesso operatore</b>?
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={() => { setPickedOperatorId(null); setStep("calendar"); }}
            className="border-slate-300"
            data-testid={`${testidPrefix}-same-op-no`}
          >
            No, mostra tutti
          </Button>
          <Button
            onClick={() => { setPickedOperatorId(previousOp.id); setStep("calendar"); }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid={`${testidPrefix}-same-op-yes`}
          >
            Sì, lo stesso
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {previousOp && (
        <button
          type="button"
          onClick={() => setStep("same_op_ask")}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid={`${testidPrefix}-back-op-choice`}
        >
          <ArrowLeft size={12} /> Cambia scelta operatore
        </button>
      )}
      {pickedOperatorId ? (
        <div className="rounded-md bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-900">
          Slot di <b>{previousOp?.first_name} {previousOp?.last_name}</b>
        </div>
      ) : (
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
          Disponibilità di <b>tutti i site operator</b>
        </div>
      )}

      {/* Iter208.63 — Toolbar mese stile Google Calendar */}
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goToday}
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
            data-testid={`${testidPrefix}-cal-today`}
          >
            Oggi
          </button>
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
            aria-label="Mese precedente"
            data-testid={`${testidPrefix}-cal-prev`}
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
            aria-label="Mese successivo"
            data-testid={`${testidPrefix}-cal-next`}
          >
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
        <div className="text-slate-900 font-normal text-lg capitalize" data-testid={`${testidPrefix}-cal-month`}>
          {monthNames[month - 1]} <span className="text-slate-500">{year}</span>
        </div>
        <div className="w-[110px]" />
      </div>

      {/* Layout a due colonne: calendario a sinistra, orari a destra */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 bg-white rounded-xl border border-slate-200 p-4">

        {/* Colonna calendario (md: 3 su 5) */}
        <div className="md:col-span-3">
          {/* Weekday header — font leggero, letter-spacing arieggiato */}
          <div className="grid grid-cols-7 text-center text-[11px] font-light tracking-wide text-slate-400 uppercase mb-2">
            {WD_MIN.map((w, i) => (
              <div key={i} className="py-1">{w}</div>
            ))}
          </div>

          {loadingSlots ? (
            <div className="text-center py-10 text-sm text-slate-500">Caricamento…</div>
          ) : (
            <div className="grid grid-cols-7 gap-y-1" data-testid={`${testidPrefix}-cal-grid`}>
              {monthGridCells.map((d, idx) => {
                const inMonth = d.getMonth() + 1 === month;
                const isToday = sameDay(d, today);
                const k = dateKey(d);
                const daySlots = groupedByDate[k] || [];
                const hasSlot = daySlots.length > 0;
                // Past = strettamente prima di oggi
                const dCopy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const todayCopy = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                const isPast = dCopy < todayCopy;
                const clickable = hasSlot && inMonth && !isPast;
                const isSelected = expandedDate === k;

                // Google Calendar–style: cerchio di 36px centrato in cella
                let circleCls = "w-9 h-9 flex items-center justify-center rounded-full text-sm transition-colors ";
                let numberCls = "";
                if (!inMonth) {
                  circleCls += "text-slate-300 ";
                } else if (isSelected) {
                  // Selezionato: cerchio pieno viola
                  circleCls += "bg-purple-600 text-white font-semibold shadow-sm ";
                } else if (isToday) {
                  // Oggi: numero in viola bold (senza background)
                  numberCls = "text-purple-600 font-bold ";
                  circleCls += "hover:bg-slate-100 " + (clickable ? "cursor-pointer " : "cursor-default ");
                } else if (isPast || !hasSlot) {
                  circleCls += "text-slate-300 cursor-not-allowed ";
                } else {
                  // Giorno del mese con slot: neutro, hover grigio (Google Calendar-like)
                  circleCls += "text-slate-800 hover:bg-slate-100 cursor-pointer ";
                }

                return (
                  <button
                    type="button"
                    key={idx}
                    disabled={!clickable}
                    onClick={() => setExpandedDate(k)}
                    className="relative aspect-square w-full flex items-center justify-center focus:outline-none"
                    data-testid={`${testidPrefix}-cal-cell-${k}`}
                    data-has-slot={hasSlot ? "1" : "0"}
                    data-is-today={isToday && inMonth ? "1" : "0"}
                    data-is-selected={isSelected && hasSlot ? "1" : "0"}
                    aria-label={
                      hasSlot
                        ? `${d.getDate()} ${monthNames[d.getMonth()]} — ${daySlots.length} orari disponibili`
                        : `${d.getDate()} ${monthNames[d.getMonth()]} — non disponibile`
                    }
                  >
                    <span className={circleCls}>
                      <span className={numberCls}>{d.getDate()}</span>
                    </span>
                    {/* Pallino verde sotto il numero se ci sono slot (solo giorni cliccabili non selezionati) */}
                    {hasSlot && inMonth && !isSelected && (
                      <span
                        className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-500"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Legenda compatta stile Google */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Disponibile
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-purple-600" />
              Selezionato
            </span>
            <span className="inline-flex items-center gap-1.5 text-purple-600 font-bold">
              15
              <span className="font-normal text-slate-500">Oggi</span>
            </span>
          </div>
        </div>

        {/* Colonna orari (md: 2 su 5) — Google Calendar chip style */}
        <div className="md:col-span-2 md:border-l md:border-slate-100 md:pl-4" data-testid={`${testidPrefix}-slots-list`}>
          {(() => {
            const list = (expandedDate && groupedByDate[expandedDate]) || [];
            if (!loadingSlots && Object.keys(groupedByDate).length === 0) {
              return (
                <div className="text-center py-8 text-sm text-slate-500">
                  Nessuno slot disponibile{pickedOperatorId ? " per questo operatore" : ""} in questo mese.
                </div>
              );
            }
            if (list.length === 0) {
              return (
                <div className="text-center text-xs text-slate-400 italic py-8 leading-relaxed">
                  Seleziona un giorno<br />
                  con il pallino verde<br />
                  per vedere gli orari.
                </div>
              );
            }
            const anyDate = new Date(`${expandedDate}T00:00:00`);
            return (
              <>
                <div className="text-sm font-medium text-slate-900 capitalize">
                  {anyDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                </div>
                <div className="text-[11px] text-slate-500 mb-3">Seleziona un orario</div>
                <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto pr-1">
                  {list.map((s) => {
                    const key = `${s.date}_${s.hour}_${s.minute || 0}`;
                    const selKey = selectedSlot
                      ? `${selectedSlot.date}_${selectedSlot.hour}_${selectedSlot.minute || 0}` : "";
                    const isSelected = key === selKey;
                    const timeLabel = `${String(s.hour).padStart(2, "0")}:${String(s.minute || 0).padStart(2, "0")}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => chooseSlot(s)}
                        className={
                          "text-xs font-medium px-3 py-1.5 rounded-full border transition-all " +
                          (isSelected
                            ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                            : "bg-white text-slate-700 border-slate-300 hover:border-purple-400 hover:bg-purple-50")
                        }
                        data-testid={`${testidPrefix}-slot-${key}`}
                        title={!pickedOperatorId && !hideOperatorName && s.best_operator?.name ? s.best_operator.name : undefined}
                      >
                        {timeLabel}
                      </button>
                    );
                  })}
                </div>
                {!pickedOperatorId && !hideOperatorName && list.some(s => s.best_operator?.name) && (
                  <p className="text-[10px] text-slate-400 mt-3">
                    Passa il mouse su un orario per vedere l'operatore proposto.
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/** Utility condivisa: converte lo slot in ISO UTC per la POST. */
export function slotToIso(slot) {
  if (!slot) return null;
  const dt = new Date(`${slot.date}T${String(slot.hour).padStart(2,"0")}:${String(slot.minute || 0).padStart(2,"0")}:00`);
  return dt.toISOString();
}

export function fmtSlot(slot) {
  if (!slot) return "";
  const dt = new Date(`${slot.date}T${String(slot.hour).padStart(2,"0")}:${String(slot.minute || 0).padStart(2,"0")}:00`);
  return dt.toLocaleString("it-IT", {
    weekday: "short", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
}
